import React, { useState, useEffect } from 'react';
import { X, Download, Share, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';

export function PWAInstallPrompt() {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if already installed (standalone mode)
    const standalone = window.matchMedia('(display-mode: standalone)').matches 
      || window.navigator.standalone === true;
    setIsStandalone(standalone);

    // Check if iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // Don't show if already installed or dismissed recently
    const dismissed = localStorage.getItem('pwa-prompt-dismissed');
    if (dismissed) {
      const dismissedTime = parseInt(dismissed, 10);
      // Don't show for 7 days after dismissal
      if (Date.now() - dismissedTime < 7 * 24 * 60 * 60 * 1000) {
        return;
      }
    }

    if (standalone) return;

    // Show iOS instructions after a delay
    if (iOS) {
      const timer = setTimeout(() => setShowPrompt(true), 3000);
      return () => clearTimeout(timer);
    }

    // Handle beforeinstallprompt for Android/Desktop
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setTimeout(() => setShowPrompt(true), 2000);
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setShowPrompt(false);
      }
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-prompt-dismissed', Date.now().toString());
  };

  if (!showPrompt || isStandalone) return null;

  return (
    <div 
      data-testid="pwa-install-prompt"
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50 animate-slide-up"
    >
      <div className="bg-slate-900 border border-slate-700 rounded-xl shadow-2xl overflow-hidden">
        <div className="p-4">
          <div className="flex items-start gap-3">
            <div className="h-12 w-12 rounded-xl bg-[#003087] flex items-center justify-center flex-shrink-0">
              <img 
                src="/icons/icon-72x72.png" 
                alt="Aviation Compliance" 
                className="h-8 w-8 rounded"
              />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-slate-100 text-sm">
                Install Aviation Compliance
              </h3>
              <p className="text-xs text-slate-400 mt-0.5">
                {isIOS 
                  ? 'Add to Home Screen for quick access with Face ID' 
                  : 'Install for quick access and offline support'}
              </p>
            </div>
            <button 
              onClick={handleDismiss}
              className="text-slate-500 hover:text-slate-300 p-1 -mr-1 -mt-1"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {isIOS ? (
            <div className="mt-4 bg-slate-800/50 rounded-lg p-3">
              <p className="text-xs text-slate-300 mb-2 font-medium">To install:</p>
              <ol className="text-xs text-slate-400 space-y-2">
                <li className="flex items-center gap-2">
                  <span className="h-5 w-5 rounded-full bg-slate-700 flex items-center justify-center text-[10px] text-slate-300 flex-shrink-0">1</span>
                  <span>Tap the <Share className="inline h-3.5 w-3.5 text-blue-400" /> Share button</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="h-5 w-5 rounded-full bg-slate-700 flex items-center justify-center text-[10px] text-slate-300 flex-shrink-0">2</span>
                  <span>Scroll and tap <Plus className="inline h-3.5 w-3.5 text-slate-300" /> Add to Home Screen</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="h-5 w-5 rounded-full bg-slate-700 flex items-center justify-center text-[10px] text-slate-300 flex-shrink-0">3</span>
                  <span>Tap Add to confirm</span>
                </li>
              </ol>
            </div>
          ) : (
            <div className="mt-3 flex gap-2">
              <Button
                onClick={handleInstall}
                className="flex-1 bg-[#003087] hover:bg-[#004db3] text-white text-sm"
              >
                <Download className="h-4 w-4 mr-2" />
                Install App
              </Button>
              <Button
                onClick={handleDismiss}
                variant="outline"
                className="border-slate-700 text-slate-300 hover:bg-slate-800 text-sm"
              >
                Not now
              </Button>
            </div>
          )}
        </div>
        
        <div className="bg-slate-800/50 px-4 py-2 border-t border-slate-800">
          <p className="text-[10px] text-slate-500 flex items-center gap-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500"></span>
            Works offline • Face ID login • Push notifications
          </p>
        </div>
      </div>
    </div>
  );
}

export default PWAInstallPrompt;
